import {Box, BoxProps} from '@viptech/react-components'
import {useRef, useState, useEffect, ReactNode, MouseEvent} from 'react'
import arrowLeft from 'src/images/arrowWhiteLeftIcon.svg'
import arrowRight from 'src/images/arrowWhiteRightIcon.svg'

interface ScrollContainerProps {
  children: ReactNode
}

const ScrollContainer = ({children, ...props}: ScrollContainerProps & BoxProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [maxScroll, setMaxScroll] = useState({left: true, right: false})

  useEffect(() => {
    const checkScrollPosition = () => {
      if (!containerRef.current) return

      const {scrollLeft, scrollWidth, clientWidth} = containerRef.current
      setMaxScroll({
        left: scrollLeft === 0,
        right: scrollLeft + clientWidth >= scrollWidth,
      })
    }

    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', checkScrollPosition)
      checkScrollPosition()
    }

    return () => {
      containerRef.current?.removeEventListener('scroll', checkScrollPosition)
    }
  }, [containerRef.current?.scrollWidth, containerRef.current?.clientWidth, children])

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(true)
    setStartX(e.pageX - containerRef.current!.offsetLeft)
    setScrollLeft(containerRef.current!.scrollLeft)
  }

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return
    e.preventDefault()
    const x = e.pageX - containerRef.current!.offsetLeft
    const walk = (x - startX) * 1.2
    containerRef.current!.scrollLeft = scrollLeft - walk
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const scroll = (direction: 'left' | 'right') => {
    if (!containerRef.current) return
    const scrollAmount = 150
    containerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  return (
    <Box position='relative' width='100%' display='flex' align='center' {...props}>
      {!maxScroll.left && (
        <Box
          position='absolute'
          left='0'
          width='20px'
          height='20px'
          backgroundColor={'#009EFF'}
          color='white'
          display='flex'
          align='center'
          justifyContent='center'
          borderRadius='50%'
          marginLeft='-10px'
          onClick={() => scroll('left')}
          style={{userSelect: 'none', cursor: 'pointer', zIndex: 10}}>
          <img src={arrowLeft} alt='ícone' />
        </Box>
      )}

      <Box
        ref={containerRef}
        style={{
          display: 'flex',
          overflowX: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          width: '100%',
        }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}>
        {children}
      </Box>

      {!maxScroll.right && (
        <Box
          position='absolute'
          right='0'
          width='20px'
          height='20px'
          backgroundColor={'#009EFF'}
          color='white'
          display='flex'
          align='center'
          justifyContent='center'
          borderRadius='50%'
          marginRight='-10px'
          onClick={() => scroll('right')}
          style={{userSelect: 'none', cursor: 'pointer', zIndex: 10}}>
          <img src={arrowRight} alt='ícone' />
        </Box>
      )}
    </Box>
  )
}

export default ScrollContainer
